import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PieChart as PieChartIcon } from 'lucide-react';

import { 
  ResponsiveContainer, 
  PieChart as RechartsPieChart, 
  Pie, 
  Cell, 
  Tooltip, 
  Legend 
} from 'recharts';

interface PieChartWidgetProps {
  config: {
    title?: string;
    dataKey?: string;
    nameKey?: string;
    colors?: string[];
    showLegend?: boolean;
    donut?: boolean;
  };
  data?: Array<Record<string, unknown>>;
}

const DEFAULT_COLORS = [
  'hsl(var(--primary))',
  'hsl(var(--secondary))',
  'hsl(150 60% 45%)',
  'hsl(45 93% 47%)',
  'hsl(280 60% 50%)',
  'hsl(200 70% 50%)',
];

export function PieChartWidget({ config, data }: PieChartWidgetProps) {
  const title = config.title || 'Distribution';
  const dataKey = config.dataKey || 'value';
  const nameKey = config.nameKey || 'name';
  const colors = config.colors || DEFAULT_COLORS;
  const chartData = data || [];

  if (chartData.length === 0) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <PieChartIcon className="h-4 w-4 text-primary" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-40">
          <p className="text-sm text-muted-foreground">No data available</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <PieChartIcon className="h-4 w-4 text-primary" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={160}>
          <RechartsPieChart>
            <Pie
              data={chartData}
              dataKey={dataKey}
              nameKey={nameKey}
              cx="50%"
              cy="50%"
              innerRadius={config.donut ? 35 : 0}
              outerRadius={55}
              paddingAngle={2}
            >
              {chartData.map((_, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={colors[index % colors.length]} 
                />
              ))}
            </Pie>
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'hsl(var(--popover))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
                fontSize: '12px'
              }}
            />
            {config.showLegend !== false && (
              <Legend 
                iconSize={8}
                wrapperStyle={{ fontSize: '10px' }}
              />
            )}
          </RechartsPieChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
